import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Tabs,
  Tab,
  Box,
  Paper,
  Typography,
  Container,
  Zoom,
  Fade,
  Badge,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import Camera from "../features/cameras/Camera";
import { useDispatch, useSelector } from "react-redux";
import {
  getCameras,
  selectCameras,
  selectCamerasLoading,
  selectCamerasError,
} from "../features/cameras/cameraSlice";
import {
  fetchAlarmDevices,
  selectAlarmDevices,
} from "../components/Alarm/alarmSlice";
import {
  fetchSensorStats,
  selectSensorStats,
} from "../features/sensors/sensorSlice";
import MiniMap from "./MiniMap";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import SecurityIcon from "@mui/icons-material/Security";
import SignalWifiStatusbarConnectedNoInternet4Icon from "@mui/icons-material/SignalWifiStatusbarConnectedNoInternet4";
import PowerOffIcon from "@mui/icons-material/PowerOff";
import AlarmManagement from "./AlarmManagement";
import WeatherStations from "./WeatherStations";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function Cameras() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [cameraTab, setCameraTab] = useState(0);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    dispatch(getCameras());
    dispatch(fetchAlarmDevices());
    dispatch(fetchSensorStats());
  }, [dispatch, retryCount]);

  const camerasData = useSelector(selectCameras);
  const loading = useSelector(selectCamerasLoading);
  const error = useSelector(selectCamerasError);

  // ✅ FIX: Wrap cameras initialization in useMemo to prevent dependency changes
  const cameras = useMemo(() => {
    return camerasData?.results || [];
  }, [camerasData]);

  const alarmDevices = useSelector(selectAlarmDevices);
  const sensorStats = useSelector(selectSensorStats);

  // ✅ FIX: Wrap defaultLocation in useMemo
  const defaultLocation = useMemo(
    () => ({ lat: 33.734457, lng: 73.045045 }),
    [],
  );
  const [center, setCenter] = useState({ center: defaultLocation, zoom: 5 });
  const [filteredCameras, setFilteredCameras] = useState([]);

  const [stats, setStats] = useState({
    armed: 0,
    offline: 0,
    unarmed: 0,
    total: 0,
  });

  const [alarmStats, setAlarmStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  // Helper function to check if camera is online (last reported within 1 hour)
  const isCameraOnline = useCallback((lastReportedAt) => {
    if (!lastReportedAt) return false;
    const lastReported = new Date(lastReportedAt);
    const now = new Date();
    const diffInHours = (now - lastReported) / (1000 * 60 * 60);
    return diffInHours <= 1;
  }, []);

  // Helper function to categorize camera
  const getCameraCategory = useCallback(
    (camera) => {
      const isLive = camera.live === true;
      const isOnline = isCameraOnline(camera.last_reported_at);

      if (isLive && isOnline) {
        return "armed";
      } else if (isLive && !isOnline) {
        return "offline";
      } else {
        return "unarmed";
      }
    },
    [isCameraOnline],
  );

  // Sort cameras by last_reported_at (newest first)
  const sortCamerasByLastReported = useCallback((camerasList) => {
    return [...camerasList].sort((a, b) => {
      const dateA = a.last_reported_at
        ? new Date(a.last_reported_at)
        : new Date(0);
      const dateB = b.last_reported_at
        ? new Date(b.last_reported_at)
        : new Date(0);
      return dateB - dateA;
    });
  }, []);

  // Apply camera filter based on tab
  const applyCameraFilter = useCallback(
    (tabValue) => {
      if (!cameras || cameras.length === 0) {
        setFilteredCameras([]);
        return;
      }

      let filtered = [];

      if (tabValue === 0) {
        // Armed: live = true AND last_reported_at within 1 hour
        filtered = cameras.filter((camera) => {
          const isLive = camera.live === true;
          const isOnline = isCameraOnline(camera.last_reported_at);
          return isLive && isOnline;
        });
      } else if (tabValue === 1) {
        // Offline: live = true AND last_reported_at older than 1 hour
        filtered = cameras.filter((camera) => {
          const isLive = camera.live === true;
          const isOnline = isCameraOnline(camera.last_reported_at);
          return isLive && !isOnline;
        });
      } else if (tabValue === 2) {
        // Unarmed: live = false (regardless of last_reported_at)
        filtered = cameras.filter((camera) => {
          return camera.live === false;
        });
      }

      // Sort by last_reported_at (newest first)
      const sortedFiltered = sortCamerasByLastReported(filtered);
      setFilteredCameras(sortedFiltered);
    },
    [cameras, isCameraOnline, sortCamerasByLastReported],
  );

  // Update stats and filtered cameras when cameras or cameraTab changes
  useEffect(() => {
    if (cameras && cameras.length > 0) {
      // Calculate stats
      let armedCount = 0;
      let offlineCount = 0;
      let unarmedCount = 0;

      cameras.forEach((camera) => {
        const category = getCameraCategory(camera);
        if (category === "armed") armedCount++;
        else if (category === "offline") offlineCount++;
        else if (category === "unarmed") unarmedCount++;
      });

      setStats({
        armed: armedCount,
        offline: offlineCount,
        unarmed: unarmedCount,
        total: cameras.length,
      });

      // Apply current filter
      applyCameraFilter(cameraTab);
    } else {
      setStats({ armed: 0, offline: 0, unarmed: 0, total: 0 });
      setFilteredCameras([]);
    }
  }, [cameras, cameraTab, applyCameraFilter, getCameraCategory]);

  // Update alarm stats when alarmDevices change
  useEffect(() => {
    if (alarmDevices?.results) {
      const active = alarmDevices.results.filter(
        (d) => d.status === "ACTIVE",
      ).length;
      const inactive = alarmDevices.results.filter(
        (d) => d.status === "INACTIVE",
      ).length;

      setAlarmStats({
        total: alarmDevices.count || alarmDevices.results.length,
        active,
        inactive,
      });
    }
  }, [alarmDevices]);

  const handleMainTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCameraTabChange = (event, newValue) => {
    setCameraTab(newValue);
  };

  const updateMapCenter = useCallback((lat, lng) => {
    let location = { lat: lat, lng: lng };
    setCenter({ center: location, zoom: 12 });
  }, []);

  const handleRetry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
  }, []);

  const renderCamerasContent = useCallback(() => {
    // Loading state
    if (loading && cameras.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress size={60} sx={{ color: "#454545", mb: 3 }} />
          <Typography variant="h6" sx={{ color: "#666" }}>
            Loading cameras...
          </Typography>
        </Box>
      );
    }

    // Error state
    if (error) {
      return (
        <Box sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleRetry}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            }
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", mb: 0.5 }}
            >
              Error loading cameras
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" align="center">
            The backend is having issues. Please try again or contact support.
          </Typography>
        </Box>
      );
    }

    // Empty state
    if (cameras.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CameraAltIcon sx={{ fontSize: 80, color: "#ccc", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "#666", mb: 1 }}>
            No cameras available
          </Typography>
          <Typography variant="body2" sx={{ color: "#999" }}>
            Please check back later or contact your administrator.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 1 }}>
        {/* Camera Filter Tabs with Legend Text */}
        <Paper
          sx={{
            borderRadius: "50px",
            overflow: "hidden",
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          <Tabs
            value={cameraTab}
            onChange={handleCameraTabChange}
            variant="fullWidth"
            sx={{
              bgcolor: "#f8f9fa",
              "& .MuiTab-root": {
                py: 2,
                transition: "all 0.3s ease",
                minHeight: "50px",
                textTransform: "none",
              },
              "& .Mui-selected": {
                bgcolor: "#454545",
                color: "white !important",
                fontWeight: 700,
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab
              icon={<SecurityIcon sx={{ fontSize: "1.5rem" }} />}
              iconPosition="start"
              label={
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    ARMED{" "}
                    <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                      (Live ON + Reported within 1 hour)
                    </Typography>
                    <Badge
                      badgeContent={stats.armed}
                      color="success"
                      sx={{
                        ml: 1,
                        "& .MuiBadge-badge": {
                          fontSize: "0.7rem",
                          height: "20px",
                          minWidth: "20px",
                          borderRadius: "10px",
                          position: "relative",
                          transform: "none",
                        },
                      }}
                    />
                  </Typography>
                </Box>
              }
              sx={{
                color: cameraTab === 0 ? "white" : "#2e7d32",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                "&.Mui-selected": {
                  color: "white !important",
                },
              }}
            />
            <Tab
              icon={
                <SignalWifiStatusbarConnectedNoInternet4Icon
                  sx={{ fontSize: "1.5rem" }}
                />
              }
              iconPosition="start"
              label={
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    OFFLINE{" "}
                    <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                      (Live ON + No report {">"} 1 hour)
                    </Typography>
                    <Badge
                      badgeContent={stats.offline}
                      color="warning"
                      sx={{
                        ml: 1,
                        "& .MuiBadge-badge": {
                          fontSize: "0.7rem",
                          height: "20px",
                          minWidth: "20px",
                          borderRadius: "10px",
                          position: "relative",
                          transform: "none",
                        },
                      }}
                    />
                  </Typography>
                </Box>
              }
              sx={{
                color: cameraTab === 1 ? "white" : "#ed6c02",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                "&.Mui-selected": {
                  color: "white !important",
                },
              }}
            />
            <Tab
              icon={<PowerOffIcon sx={{ fontSize: "1.5rem" }} />}
              iconPosition="start"
              label={
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    UNARMED{" "}
                    <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                      (Live OFF (disarmed))
                    </Typography>
                    <Badge
                      badgeContent={stats.unarmed}
                      color="error"
                      sx={{
                        ml: 1,
                        "& .MuiBadge-badge": {
                          fontSize: "0.7rem",
                          height: "20px",
                          minWidth: "20px",
                          borderRadius: "10px",
                          position: "relative",
                          transform: "none",
                        },
                      }}
                    />
                  </Typography>
                </Box>
              }
              sx={{
                color: cameraTab === 2 ? "white" : "#d32f2f",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                "&.Mui-selected": {
                  color: "white !important",
                },
              }}
            />
          </Tabs>
        </Paper>

        {/* Camera Grid */}
        <Grid
          container
          spacing={2}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 1,
          }}
        >
          {filteredCameras.length === 0 ? (
            <Grid item xs={12}>
              <Fade in={true}>
                <Paper
                  sx={{
                    textAlign: "center",
                    bgcolor: "#f8f9fa",
                    borderRadius: "20px",
                    p: 4,
                  }}
                >
                  <Typography variant="h6" sx={{ color: "#666", mb: 1 }}>
                    {cameraTab === 0
                      ? "No armed cameras available"
                      : cameraTab === 1
                        ? "No offline cameras available"
                        : "No unarmed cameras available"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#999" }}>
                    {cameraTab === 0
                      ? "All cameras are either offline or unarmed"
                      : cameraTab === 1
                        ? "All live cameras are reporting within the last hour"
                        : "No cameras are currently disarmed"}
                  </Typography>
                </Paper>
              </Fade>
            </Grid>
          ) : (
            <>
              {filteredCameras.map((camera, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={2.4}
                  xl={2.4}
                  key={camera.id || index}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Zoom
                    in={true}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      <Camera
                        content={camera}
                        latestEvent={
                          camera.latest_event
                            ? `${camera.latest_event}`
                            : "https://storage.googleapis.com/proudcity/mebanenc/uploads/2021/03/placeholder-image-300x225.png"
                        }
                        updateMapCenter={updateMapCenter}
                      />
                    </Box>
                  </Zoom>
                </Grid>
              ))}

              {/* Map - Always show map */}
              <Grid
                item
                xs={12}
                md={12}
                key="map"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  mt: 1,
                  px: 0,
                }}
              >
                <Fade
                  in={true}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      width: {
                        xs: "95%",
                        sm: "80%",
                        md: "70%",
                        lg: "60%",
                      },
                      height: "260px",
                      borderRadius: "25px",
                      overflow: "hidden",
                      p: 0,
                      m: 0,
                      "& .MuiPaper-root": {
                        p: 0,
                      },
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.02)",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                      },
                    }}
                  >
                    <MiniMap
                      camera={filteredCameras}
                      defaultCenter={center}
                      containerStyle={{
                        width: "100%",
                        height: "100%",
                        margin: 0,
                        padding: 0,
                      }}
                    />
                  </Paper>
                </Fade>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    );
  }, [
    loading,
    cameras,
    error,
    handleRetry,
    cameraTab,
    stats,
    filteredCameras,
    updateMapCenter,
    center,
  ]);

  return (
    <div className="page">
      <div className="page__content">
        <div className="main-wrapper">
          <Container maxWidth="xl" sx={{ mt: 1, mb: 2 }}>
            {/* Main Header with Tabs */}
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                mb: 1,
                backgroundColor: "white",
                borderRadius: "50px",
                boxShadow: "0 2px 20px rgba(0,0,0,0.05)",
                overflow: "hidden",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleMainTabChange}
                variant="fullWidth"
                sx={{
                  "& .MuiTab-root": {
                    py: 2.5,
                    fontSize: { xs: "0.85rem", sm: "1rem" },
                    fontWeight: 600,
                    transition: "all 0.3s ease",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#454545",
                    color: "white !important",
                  },
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}
              >
                <Tab
                  icon={<CameraAltIcon sx={{ mr: 1 }} />}
                  iconPosition="start"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Cameras
                      {!error && (
                        <Badge
                          badgeContent={stats.total}
                          color="error"
                          sx={{ ml: 2 }}
                        />
                      )}
                    </Box>
                  }
                />
                <Tab
                  icon={<NotificationsActiveIcon sx={{ mr: 1 }} />}
                  iconPosition="start"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Alarms
                      <Badge
                        badgeContent={alarmStats.total}
                        color="error"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  }
                />
                <Tab
                  icon={<WbSunnyIcon sx={{ mr: 1 }} />}
                  iconPosition="start"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Weather Stations
                      <Badge
                        badgeContent={sensorStats.total}
                        color="error"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            {/* Content based on active tab */}
            <Fade in={true} timeout={500}>
              <Box>
                {activeTab === 0 && renderCamerasContent()}
                {activeTab === 1 && <AlarmManagement />}
                {activeTab === 2 && <WeatherStations />}
              </Box>
            </Fade>
          </Container>
        </div>
      </div>
    </div>
  );
}
